import { isEmpty } from "lodash";
import { memberTabFilters } from "@/react/components/SettingsApp/ManageMembers/TabContent/Filters/memberFilters";
import { usePickFilters } from "@/react/components/SettingsApp/ManageMembers/TabContent/Filters/usePickFilters";
import { usePageQuery } from "@/react/components/SettingsApp/ManageMembers/TabContent/usePageQuery";
import { useFilterParams } from "@/react/components/SettingsApp/ManageMembers/useFilterParams";
import { usePunditUserContext } from "@/react/contexts/punditUserContext";
import { isCommunityAdmin } from "@/react/helpers/communityMemberHelpers";
import { getOrderParamFromSort } from "@/react/helpers/urlHelpers/getOrderParamFromSort";
import { useAudienceInfiniteQuery } from "@/react/hooks/useAudienceInfiniteQuery";
import { getPageSize } from "@/react/hooks/useCommunityMembersInfiniteQueryV2";
import { useContactsSearchEnabledForMembersAndSpaces } from "@/react/hooks/useContactsSearchEnabledForMembersAndSpaces";
import { useMdScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { createBaseFilter } from "../../../MemberFilters/types";
import { ViewTypes } from "../ListView/CommunityMember/MemberActions";
import { useMembersInfiniteQuery } from "./useMembersInfiniteQuery";
import { useMembersInfiniteQueryV2 } from "./useMembersInfiniteQueryV2";

export interface UseSelectMembersInfiniteQueryProps {
  defaultSort: string;
  scopeResults: object;
  viewType: string;
}

export const memberBaseFilter = [
  createBaseFilter({
    key: "member",
    value: "true",
  }),
];

export const useSelectMembersInfiniteQuery = ({
  defaultSort,
  scopeResults,
  viewType,
}: UseSelectMembersInfiniteQueryProps) => {
  const isMd = useMdScreenMediaQuery();
  const isContactsSearchEnabled = useContactsSearchEnabledForMembersAndSpaces();
  const { initialPage } = usePageQuery();
  const { filters, scope, query } = useFilterParams(0);
  const { currentCommunityMember } = usePunditUserContext();
  const isAdmin = isCommunityAdmin(currentCommunityMember);

  const order = getOrderParamFromSort(defaultSort);
  const pickedFilters = usePickFilters({
    allFilters: memberTabFilters,
    selectedFilters: filters,
  });
  const finalFilters = [...pickedFilters, ...memberBaseFilter];

  const audienceInfiniteQuery = useAudienceInfiniteQuery({
    perPage: getPageSize(),
    order,
    query,
    initialPage,
    options: {
      keepPreviousData: false,
      enabled: isContactsSearchEnabled,
    },
    filters: finalFilters,
    scope,
    isAdminScope: isAdmin,
  });

  const desktopMemberParams = useMembersInfiniteQueryV2({
    defaultSort,
    scopeResults,
    per_page: getPageSize(),
    enabled: !isContactsSearchEnabled,
  });

  const mobileMemberParams = useMembersInfiniteQuery({
    defaultSort,
    scopeResults,
  });

  if (viewType !== ViewTypes.MEMBER_DIRECTORY || !isMd) {
    return mobileMemberParams;
  }

  if (isContactsSearchEnabled) {
    return {
      ...audienceInfiniteQuery,
      dataLength: audienceInfiniteQuery.totalQueryAudience,
      totalMembers: audienceInfiniteQuery.totalAudience,
      members: audienceInfiniteQuery.audience,
      isActiveQuery: !isEmpty(filters),
    };
  }

  return desktopMemberParams;
};

export type UseSelectMembersInfiniteQuery = ReturnType<
  typeof useSelectMembersInfiniteQuery
>;
